import { useState } from "react";
import { X } from "lucide-react";

const TABS = {
  TERMS: "terms",
  PRIVACY: "privacy",
};

const TermsContent = () => (
  <div className="font-popinsRegular text-black/70 text-sm space-y-5 text-left">
    <p className="text-xs text-black/40 font-popinsMedium">Effective Date: May 6, 2026</p>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">1. Acceptance of Terms</h3>
      <p>
        By accessing or using the TCCplus People In Need application ("the App"), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the App.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">2. About the Platform</h3>
      <p>
        TCCplus People In Need is part of the County Connect Plus (TCCplus) platform, designed to connect individuals with local non-profit organizations offering essential community resources and services. The platform enables non-profits to post events and resources and allows users to discover and inquire about available services.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">3. Account Registration</h3>
      <p>
        To access certain features, non-profit organizations must register for an account and submit required documentation (such as an IRS Determination Letter). You agree to:
      </p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Provide accurate and complete information during registration</li>
        <li>Keep your account credentials confidential</li>
        <li>Notify us immediately of any unauthorized use of your account</li>
        <li>Take responsibility for all activity that occurs under your account</li>
      </ul>
      <p className="mt-2">
        Account approval is subject to review by platform administrators. We reserve the right to reject or revoke any account at our discretion.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">4. Permitted Use</h3>
      <p>You may use the App solely for lawful purposes and in accordance with these Terms. You agree to use the platform to:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Connect communities with legitimate non-profit services</li>
        <li>Post accurate event and resource information (non-profits)</li>
        <li>Send genuine inquiries to non-profit organizations</li>
        <li>Collaborate with other verified non-profit organizations</li>
      </ul>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">5. Prohibited Activities</h3>
      <p>You agree NOT to:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Post false, misleading, or fraudulent information</li>
        <li>Use the App for any commercial solicitation unrelated to non-profit services</li>
        <li>Harass, threaten, or harm other users or organizations</li>
        <li>Attempt to gain unauthorized access to any part of the platform</li>
        <li>Upload malicious code, viruses, or harmful content</li>
        <li>Violate any applicable local, state, or federal laws or regulations</li>
        <li>Impersonate any person, organization, or entity</li>
      </ul>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">6. Content Responsibility</h3>
      <p>
        Non-profit organizations are solely responsible for the accuracy, legality, and appropriateness of content they post on the platform, including event descriptions, resource listings, and any communications with users. TCCplus does not verify the content of postings and disclaims liability for any inaccuracies.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">7. Intellectual Property</h3>
      <p>
        All content, design, logos, and trademarks associated with the TCCplus platform are the property of County Connect Plus or its licensors. You may not reproduce, distribute, or create derivative works without prior written consent.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">8. Disclaimers</h3>
      <p>
        The App is provided on an "as is" and "as available" basis. We make no warranties, express or implied, regarding the availability, accuracy, or reliability of the platform. TCCplus is not responsible for the services provided by listed non-profit organizations or any outcome resulting from your interactions with them.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">9. Limitation of Liability</h3>
      <p>
        To the fullest extent permitted by law, TCCplus County Connect Plus shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of or inability to use the App.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">10. Termination</h3>
      <p>
        We reserve the right to suspend or terminate your account and access to the App at any time, with or without notice, for conduct that we believe violates these Terms or is harmful to other users, third parties, or the platform.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">11. Changes to Terms</h3>
      <p>
        We may update these Terms of Use from time to time. We will notify you of significant changes through the App or via email. Your continued use of the App after changes take effect constitutes your acceptance of the revised Terms.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">12. Contact Us</h3>
      <p>If you have any questions about these Terms of Use, please contact us at:</p>
      <p className="mt-1 font-popinsMedium text-[#006077]">
        TCCplus County Connect Plus<br />
        Email: info@countyconnectplus.com
      </p>
    </section>
  </div>
);

const PrivacyContent = () => (
  <div className="font-popinsRegular text-black/70 text-sm space-y-5 text-left">
    <p className="text-xs text-black/40 font-popinsMedium">Effective Date: May 6, 2026</p>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">1. Introduction</h3>
      <p>
        Welcome to the TCCplus Non-Profit Portal ("the Portal"), a web-based platform developed as part of the County Connect Plus (TCCplus) platform. This Privacy Policy explains how we collect, use, store, and protect information provided by registered non-profit organizations when using the Portal.
      </p>
      <p className="mt-2">
        By registering your organization or using the Portal, you agree to the practices described in this Privacy Policy. If you do not agree, please do not use the Portal.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">2. About the Portal</h3>
      <p>
        The TCCplus Non-Profit Portal is a powerful management dashboard exclusively for verified non-profit organizations. It enables organizations to:
      </p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Monitor a real-time command center dashboard with engagement metrics and active listings</li>
        <li>Create and publish detailed event listings with banner images, descriptions, dates, and times</li>
        <li>Manage and organize all published events and resources from a single interface</li>
        <li>Receive and respond to inquiries submitted by community members through the TCCplus People In Need app</li>
        <li>Store and manage organizational documents privately in a Personal Space with selective visibility controls</li>
        <li>Collaborate with other verified non-profit organizations on the platform</li>
      </ul>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">3. Information We Collect</h3>
      <h4 className="font-popinsMedium text-black/70 mt-2 mb-1">3.1 Organization Registration Information</h4>
      <p>When registering your non-profit organization, we collect:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Organization name</li>
        <li>Employer Identification Number (EIN / Tax ID)</li>
        <li>Organization address and ZIP code</li>
        <li>Phone number</li>
        <li>Administrator email address and password (stored in encrypted form)</li>
        <li>IRS Determination Letter or registration document (uploaded at signup)</li>
        <li>Services offered by the organization</li>
      </ul>
      <h4 className="font-popinsMedium text-black/70 mt-3 mb-1">3.2 Content You Publish</h4>
      <p>When using the Portal's publishing tools, we collect and store:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Event details: name, description, banner images, date, time, and location</li>
        <li>Resource listings and associated details</li>
        <li>Documents uploaded to your Personal Space (PDFs, images, and other files)</li>
        <li>Visibility settings applied to Personal Space documents</li>
      </ul>
      <h4 className="font-popinsMedium text-black/70 mt-3 mb-1">3.3 Inquiry and Communication Data</h4>
      <p>
        When community members submit inquiries through the TCCplus People In Need app, their messages are routed to your Portal dashboard. We store inquiry content, timestamps, and your organization's responses to maintain a full communication history.
      </p>
      <h4 className="font-popinsMedium text-black/70 mt-3 mb-1">3.4 Automatically Collected Information</h4>
      <p>
        We may automatically collect limited technical data to maintain Portal performance and security, including browser type, session duration, pages visited, and error logs. This data is used solely to improve platform stability and is not linked to individual user identities beyond the organization account.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">4. How We Use Your Information</h3>
      <p>We use the information we collect to:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Review and approve your organization's registration on the platform</li>
        <li>Provide access to the Portal dashboard and all its management tools</li>
        <li>Display your published events and resources to community members via the People In Need app</li>
        <li>Route community inquiries to your organization's dashboard for timely responses</li>
        <li>Generate engagement metrics, event statistics, and activity summaries within your dashboard</li>
        <li>Store and manage documents uploaded to your Personal Space</li>
        <li>Send platform notifications relevant to your organization's activity</li>
        <li>Improve Portal features, performance, and user experience</li>
        <li>Ensure the security and integrity of the platform</li>
      </ul>
      <p className="mt-2">We do not sell, rent, or trade your organization's information to any third parties.</p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">5. How We Share Your Information</h3>
      <h4 className="font-popinsMedium text-black/70 mt-2 mb-1">5.1 With the Public (via People In Need App)</h4>
      <p>
        Event listings and resource information that your organization publishes on the Portal will be visible to community members through the TCCplus People In Need mobile app. This is the intended purpose of publishing content on the platform.
      </p>
      <h4 className="font-popinsMedium text-black/70 mt-3 mb-1">5.2 With Other Non-Profit Organizations</h4>
      <p>
        Documents stored in your Personal Space are private by default. You may choose to make specific documents visible to other verified non-profit organizations on the platform. Only documents you explicitly set to a shared visibility will be accessible to other organizations.
      </p>
      <h4 className="font-popinsMedium text-black/70 mt-3 mb-1">5.3 With Platform Administrators</h4>
      <p>
        TCCplus platform administrators may access your organization's registration information and platform activity for the purposes of account review, approval, moderation, and support.
      </p>
      <h4 className="font-popinsMedium text-black/70 mt-3 mb-1">5.4 Service Providers</h4>
      <p>
        We may engage trusted third-party service providers (such as cloud hosting and analytics providers) to help operate the Portal. These providers are contractually required to keep your information confidential and may only use it to perform services on our behalf.
      </p>
      <h4 className="font-popinsMedium text-black/70 mt-3 mb-1">5.5 Legal Requirements</h4>
      <p>
        We may disclose your information if required by law, court order, or to protect the safety and rights of users or the public.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">6. Document and File Storage</h3>
      <p>
        Files uploaded to the Portal — including IRS Determination Letters submitted at registration and documents stored in your Personal Space — are retained securely for as long as your account is active. You may delete documents from your Personal Space at any time. Registration documents are retained for compliance and verification purposes even after account deletion.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">7. Data Retention</h3>
      <p>
        We retain your organization's information for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time by contacting us. Upon receiving a valid deletion request, we will remove your data within 30 days, except where retention is required for legal or compliance purposes.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">8. Data Security</h3>
      <p>We implement appropriate technical and organizational measures to protect your data, including:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Encrypted storage of passwords and sensitive credentials</li>
        <li>Secure HTTPS connections for all data transmission</li>
        <li>Access controls limiting data visibility to authorized users</li>
        <li>Regular security reviews of the platform</li>
      </ul>
      <p className="mt-2">
        While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">9. Your Rights</h3>
      <p>As a registered organization on the Portal, you may have the following rights regarding your data:</p>
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li><span className="font-popinsMedium">Right to Access</span> - Request a copy of the information we hold about your organization</li>
        <li><span className="font-popinsMedium">Right to Correction</span> - Update or correct your organization's profile information via User Settings</li>
        <li><span className="font-popinsMedium">Right to Deletion</span> - Request deletion of your account and associated data</li>
        <li><span className="font-popinsMedium">Right to Withdraw Consent</span> - Withdraw consent for data processing where applicable</li>
      </ul>
      <p className="mt-2">To exercise any of these rights, please contact us using the details in the Contact Us section below.</p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">10. Third-Party Links</h3>
      <p>
        The Portal may reference or link to external websites or resources. We are not responsible for the privacy practices or content of those external sites and encourage you to review their privacy policies independently.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">11. Changes to This Privacy Policy</h3>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we make changes, we will update the "Effective Date" at the top and notify registered organizations through the Portal or via email. Continued use of the Portal after changes take effect constitutes acceptance of the updated policy.
      </p>
    </section>

    <section>
      <h3 className="font-popinsSemiBold text-black/80 text-base mb-1">12. Contact Us</h3>
      <p>If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us at:</p>
      <p className="mt-1 font-popinsMedium text-[#006077]">
        Email: info@countyconnectplus.com
      </p>
    </section>
  </div>
);

const TermsAndPrivacyModal = ({ isOpen, defaultTab = TABS.TERMS, onClose }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  useState(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h2 className="font-popinsBold text-lg text-black/80">
            {activeTab === TABS.TERMS ? "Terms of Use" : "Privacy Policy"}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors duration-200 text-black/40 hover:text-black/70"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-6 pt-4 gap-2 border-b border-gray-100 pb-0">
          <button
            onClick={() => setActiveTab(TABS.TERMS)}
            className={`pb-3 px-1 text-sm font-popinsMedium border-b-2 transition-all duration-200 ${
              activeTab === TABS.TERMS
                ? "border-[#3FC4E3] text-[#3FC4E3]"
                : "border-transparent text-black/40 hover:text-black/60"
            }`}
          >
            Terms of Use
          </button>
          <button
            onClick={() => setActiveTab(TABS.PRIVACY)}
            className={`pb-3 px-1 text-sm font-popinsMedium border-b-2 transition-all duration-200 ${
              activeTab === TABS.PRIVACY
                ? "border-[#3FC4E3] text-[#3FC4E3]"
                : "border-transparent text-black/40 hover:text-black/60"
            }`}
          >
            Privacy Policy
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {activeTab === TABS.TERMS ? <TermsContent /> : <PrivacyContent />}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gradient-to-b from-[#3FC4E3] to-[#006077] text-white font-popinsSemiBold text-sm rounded-md shadow transition-all duration-300 hover:scale-105 hover:shadow-[0_0_15px_rgba(63,196,227,0.5)] active:scale-95"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export { TABS };
export default TermsAndPrivacyModal;
